/**
 * OrgUserCreateModal Component
 *
 * Modal dialog for organization owners to pre-register users in federated organizations.
 * Supports both create mode (email + name + send_invite) and edit mode (name, role, is_active).
 * Users created this way must login to complete their profile (via federated SSO).
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  Dropdown,
  Option,
  Switch,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../common/AsyncButton';
import { Alert } from '../common/Alert';
import { DualListPicker } from '../common/DualListPicker';
import { OrganizationApiService } from '../../services/organizationApi';
import type { OrganizationUser, OrganizationTenant, CreatedOrgUser } from '../../types/organization';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  checkboxField: {
    marginTop: tokens.spacingVerticalS,
  },
  switchField: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: tokens.spacingVerticalS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: tokens.spacingVerticalM,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  successContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  tapCode: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    textAlign: 'center' as const,
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

type OrgUserRole = 'organization_owner' | 'organization_admin' | 'organization_user';

interface OrgUserCreateModalProps {
  open: boolean;
  orgId: number;
  onClose: () => void;
  onSuccess: () => void;
  user?: OrganizationUser;
  /** Organization tenants for tenant access selection (edit mode) */
  organizationTenants?: OrganizationTenant[];
  /** Callback to open the add tenant dialog (optional) */
  onAddTenantClick?: () => void;
  /** Organization's primary domain for detecting external users */
  primaryDomain?: string;
}

export function OrgUserCreateModal({
  open,
  orgId,
  onClose,
  onSuccess,
  user,
  organizationTenants = [],
  onAddTenantClick,
  primaryDomain,
}: OrgUserCreateModalProps) {
  const styles = useStyles();
  const isEditMode = !!user;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<OrgUserRole>('organization_user');
  const [isActive, setIsActive] = useState(true);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUser, setCreatedUser] = useState<CreatedOrgUser | null>(null);

  // Reset/populate form when modal opens or user changes
  useEffect(() => {
    if (open) {
      if (user) {
        setEmail(user.email ?? '');
        setName(user.name);
        setRole(user.role as OrgUserRole);
        setIsActive(user.is_active);
        setSelectedTenantIds(user.accessible_tenant_ids || []);
      } else {
        setEmail('');
        setName('');
        setRole('organization_user');
        setIsActive(true);
        setSelectedTenantIds([]);
      }
      setError(null);
      setIsSubmitting(false);
      setCreatedUser(null);
    }
  }, [open, user]);

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      if (isEditMode && user) {
        // Update user info
        await OrganizationApiService.updateOrgUser(orgId, user.id, {
          name,
          role,
          is_active: isActive,
        });

        // Update tenant access if there are tenants available
        if (organizationTenants.length > 0) {
          const originalTenantIds = user.accessible_tenant_ids || [];
          const tenantAccessChanged =
            selectedTenantIds.length !== originalTenantIds.length ||
            selectedTenantIds.some(id => !originalTenantIds.includes(id));

          if (tenantAccessChanged) {
            await OrganizationApiService.updateUserTenantAccess(
              orgId,
              user.id,
              selectedTenantIds
            );
          }
        }
      } else {
        // Create the user - backend will create invitation for federated orgs or user for home_tenant orgs
        // The API returns either a user or invitation object depending on org authentication_mode
        await OrganizationApiService.createOrgUser(orgId, {
          email,
          name,
          role,
          send_invite: true,
        });
        // Note: Tenant access can be assigned after the user accepts the invitation (in edit mode)
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} user`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Check if the email domain is outside the organization
  const isExternalUser = (() => {
    if (!primaryDomain || !email.includes('@')) return false;
    const emailDomain = email.split('@')[1]?.toLowerCase();
    return emailDomain && emailDomain !== primaryDomain.toLowerCase();
  })();

  const isFormValid = isEditMode
    ? name.trim() !== ''
    : email.trim() !== '' && name.trim() !== '';

  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {createdUser ? 'User Created' : isEditMode ? 'Edit User' : 'Add User'}
          </DialogTitle>
          {createdUser ? (
            <>
              <DialogContent className={styles.successContent}>
                <Alert intent="success">
                  User created successfully. Share these credentials with the user.
                </Alert>
                <div className={styles.infoRow}>
                  <Text className={styles.label}>User Principal Name</Text>
                  <Text>{createdUser.user_principal_name}</Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.label}>Temporary Access Pass</Text>
                  <div className={styles.tapCode}>{createdUser.tap_code}</div>
                </div>
                {createdUser.tap_expires_at && (
                  <div className={styles.infoRow}>
                    <Text className={styles.label}>TAP Expires</Text>
                    <Text>{new Date(createdUser.tap_expires_at).toLocaleString()}</Text>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button appearance="primary" onClick={onClose}>
                  Done
                </Button>
              </DialogActions>
            </>
          ) : (
            <>
              <DialogContent className={styles.content}>
                {error && <Alert intent="error">{error}</Alert>}
                <Field label="Email or UPN" required={!isEditMode}>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting || isEditMode}
                    placeholder="user@example.com or user@tenant.onmicrosoft.com"
                  />
                </Field>
                {isExternalUser && (
                  <Alert intent="info">
                    This user is outside your organization. An IRPilot account will be created for them.
                  </Alert>
                )}
                <Field label="Name" required>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Full Name"
                  />
                </Field>
                <Field label="Role">
                  <Dropdown
                    value={
                      role === 'organization_owner'
                        ? 'Owner'
                        : role === 'organization_admin'
                          ? 'Admin'
                          : 'User'
                    }
                    selectedOptions={[role]}
                    onOptionSelect={(_, data) => {
                      if (data.optionValue) {
                        setRole(data.optionValue as OrgUserRole);
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    <Option value="organization_user">User</Option>
                    <Option value="organization_admin">Admin</Option>
                    <Option value="organization_owner">Owner</Option>
                  </Dropdown>
                </Field>
                {isEditMode ? (
                  <div className={styles.switchField}>
                    <span>Account Enabled</span>
                    <Switch
                      checked={isActive}
                      onChange={(_, data) => setIsActive(data.checked)}
                      disabled={isSubmitting}
                    />
                  </div>
                ) : (
                  <Alert intent="info">
                    An invitation email will be sent to this user. They must click
                    the link in the email and sign in with their corporate account
                    to verify their identity and activate their account. After
                    activation, you can assign them to tenants.
                  </Alert>
                )}
                {/* Tenant Access Section - only shown in edit mode */}
                {isEditMode && (
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <Text className={styles.sectionTitle}>Tenant Access</Text>
                    </div>
                    {organizationTenants.length > 0 ? (
                      <DualListPicker
                        availableItems={organizationTenants.map(tenant => ({
                          id: tenant.tenant_id,
                          label: tenant.tenant_name,
                        }))}
                        selectedIds={selectedTenantIds}
                        onSelectionChange={setSelectedTenantIds}
                        availableLabel="Available Tenants"
                        selectedLabel="Assigned Tenants"
                      />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: tokens.spacingVerticalS }}>
                        <Text style={{ color: tokens.colorNeutralForeground3, alignSelf: 'flex-start' }}>
                          No tenants have been assigned to this organization yet.
                        </Text>
                        {onAddTenantClick && (
                          <Button
                            appearance="secondary"
                            onClick={() => {
                              onClose();
                              onAddTenantClick();
                            }}
                          >
                            Add Tenant
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="secondary"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <AsyncButton
                  appearance="primary"
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                  loading={isSubmitting}
                  loadingText={isEditMode ? 'Saving...' : 'Creating...'}
                >
                  {isEditMode ? 'Save Changes' : 'Create User'}
                </AsyncButton>
              </DialogActions>
            </>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
